import React, { useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { SpeciesManagement } from '../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';
import { Session } from '../peer-colab-model/Session';

interface SpeciesOption {
  id: string;
  name: string;
}
interface BreedOption {
  id: string;
  name: string;
}

interface SpeciesBreedSelectorProps {
  onSelect: (speciesId: string, breedIds: string[]) => void;
  disabled?: boolean;
  initialSpeciesId?: string;
  initialBreedIds?: string[];
}

const SpeciesBreedSelector: React.FC<SpeciesBreedSelectorProps> = ({ onSelect, disabled, initialSpeciesId, initialBreedIds }) => {
  const [speciesOptions, setSpeciesOptions] = useState<SpeciesOption[]>([]);
  const [selectedSpecies, setSelectedSpecies] = useState<SpeciesOption | null>(null);
  const [breedOptions, setBreedOptions] = useState<BreedOption[]>([]);
  const [selectedBreeds, setSelectedBreeds] = useState<BreedOption[]>([]);
  // Prefill selected species and breeds if props provided
  useEffect(() => {
    if (speciesOptions.length > 0 && initialSpeciesId) {
      const foundSpecies = speciesOptions.find(s => s.id === initialSpeciesId) || null;
      setSelectedSpecies(foundSpecies);
    }
  }, [speciesOptions, initialSpeciesId]);

  useEffect(() => {
    if (breedOptions.length > 0 && initialBreedIds && initialBreedIds.length > 0) {
      setSelectedBreeds(breedOptions.filter(b => initialBreedIds.includes(b.id)));
    }
  }, [breedOptions, initialBreedIds]);
  const [loadingSpecies, setLoadingSpecies] = useState(false);
  const [loadingBreeds, setLoadingBreeds] = useState(false);

  useEffect(() => {
    async function fetchSpeciesOptions() {
      setLoadingSpecies(true);
      const result = await Session.getClient().request(SpeciesManagement.searchSpecies({ query: '', limit: 50, offset: 0 }));
      result.maybeOk((list) => {
        setSpeciesOptions(list.map(s => ({ id: s.id, name: s.name })));
      });
      setLoadingSpecies(false);
    }
    fetchSpeciesOptions();
  }, []);

  useEffect(() => {
    async function fetchBreedOptions() {
      if (!selectedSpecies) {
        setBreedOptions([]);
        return;
      }
      setLoadingBreeds(true);
      const result = await Session.getClient().addPathParam('speciesId', selectedSpecies.id).request(SpeciesManagement.getSpecies());
      result.maybeOk((species) => {
        setBreedOptions((species.breeds ?? []).map(b => ({ id: b.breed.id, name: b.breed.name })));
      });
      setLoadingBreeds(false);
    }
    fetchBreedOptions();
  }, [selectedSpecies]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Autocomplete
        options={speciesOptions}
        getOptionLabel={option => option.name}
        value={selectedSpecies}
        onChange={(_, value) => {
          setSelectedSpecies(value);
          setSelectedBreeds([]);
          if (value) {
            onSelect(value.id, []);
          } else {
            onSelect('', []);
          }
        }}
        loading={loadingSpecies}
        disabled={disabled}
        sx={{ minWidth: 180 }}
        renderInput={params => <TextField {...params} label="Select Species" variant="outlined" size="small" />}
      />
      <Autocomplete
        multiple
        options={breedOptions}
        getOptionLabel={option => option.name}
        value={selectedBreeds}
        onChange={(_, value) => {
          setSelectedBreeds(value);
          if (selectedSpecies) {
            onSelect(selectedSpecies.id, value.map(b => b.id));
          }
        }}
        loading={loadingBreeds}
        disabled={disabled || !selectedSpecies}
        sx={{ minWidth: 220 }}
        renderInput={params => <TextField {...params} label="Select Breeds" variant="outlined" size="small" />}
      />
    </Box>
  );
};

export default SpeciesBreedSelector;
